import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppService } from '../../app.service';
import { isArrayWithData } from '../../utilities/array.helpers';
import moment from 'moment';

@Injectable({
    providedIn: 'root',
})
export class DashboardService {
    private _baseUrl: string = '';

    public constructor(
        public appService: AppService,
        public http: HttpClient,
        @Inject('API_BASE_URL') baseurl: string,
    ) {
        this._baseUrl = baseurl;
    }

    public getCreditors = (): Observable<any> => {
        const apipath: string = 'dashboard/lookups/creditor';
        const fullPath: string = this._baseUrl + apipath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };

    public getTimezones = (): Observable<any> => {
        const apipath: string = 'dashboard/lookups/timezone';
        const fullPath: string = this._baseUrl + apipath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };

    public getPaymentTypes = (): Observable<any> => {
        const apipath: string = 'dashboard/lookups/paymenttype';
        const fullPath: string = this._baseUrl + apipath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };

    public getPaymentStatuses = (): Observable<any> => {
        const apipath: string = 'dashboard/lookups/paymentstatus';
        const fullPath: string = this._baseUrl + apipath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };

    public getTransactionTypes = (): Observable<any> => {
        const apipath: string = 'dashboard/lookups/transactiontype';
        const fullPath: string = this._baseUrl + apipath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };

    public getChannels = (): Observable<any> => {
        const apipath: string = 'dashboard/lookups/channel';
        const fullPath: string = this._baseUrl + apipath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };

    public getStatusNames = (): Observable<any> => {
        const apipath: string = 'dashboard/lookups/communicationstatus';
        const fullPath: string = this._baseUrl + apipath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };

    public getNotificationTypes = (): Observable<any> => {
        const apipath: string = 'dashboard/lookups/notificationtype';
        const fullPath: string = this._baseUrl + apipath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };

    public getGraphPayments = (
        fromDate: string,
        toDate: string,
        creditorIds?: number[],
        timezoneOffset?: string,
        paymentTypes?: number[],
        paymentStatuses?: number[],
        totalType: number = 1,
    ): Observable<any> => {
        let apiPath: string =
            'dashboard/payments?' +
            'FromDate=' +
            fromDate +
            '&ToDate=' +
            toDate +
            '&TotalType=' +
            totalType;

        if (timezoneOffset) {
            apiPath += '&TZ=' + timezoneOffset;
        }

        const CreditorIds = isArrayWithData(creditorIds)
            ? creditorIds!.join(',')
            : '';

        const PaymentTypes = isArrayWithData(paymentTypes)
            ? paymentTypes!.join(',')
            : '';

        const PaymentStatuses = isArrayWithData(paymentStatuses)
            ? paymentStatuses!.join(',')
            : '';

        if (CreditorIds) {
            apiPath += '&CreditorIds=' + CreditorIds;
        }
        if (PaymentTypes) {
            apiPath += '&PaymentTypes=' + PaymentTypes;
        }

        if (PaymentStatuses) {
            apiPath += '&PaymentStatuses=' + PaymentStatuses;
        }

        const fullPath: string = this._baseUrl + apiPath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };

    public getGraphTrendAnalysis = (
        fromDate: string,
        toDate: string,
        creditorIds?: number[],
        timezoneOffset?: string,
        transactionTypes?: number[],
        paymentStatus?: number,
        groupBy: number = 0,
        analysisType: string = 'payments',
        channels?: number[],
        statusNames?: number[],
    ): Observable<any> => {
        let apiPath: string =
            'dashboard/' +
            analysisType +
            '/trend?' +
            'FromDate=' +
            fromDate +
            '&ToDate=' +
            toDate +
            '&PaymentStatus=' +
            paymentStatus +
            '&GroupBy=' +
            groupBy;

        if (timezoneOffset) {
            apiPath += '&TZ=' + timezoneOffset;
        }

        const CreditorIds = isArrayWithData(creditorIds)
            ? creditorIds!.join(',')
            : '';
        if (CreditorIds) {
            apiPath += '&CreditorIds=' + CreditorIds;
        }

        const TransactionTypes = isArrayWithData(transactionTypes)
            ? transactionTypes!.join(',')
            : '';
        if (TransactionTypes) {
            apiPath += '&TransactionTypes=' + TransactionTypes;
        }

        const Channels = isArrayWithData(channels) ? channels!.join(',') : '';
        if (Channels) {
            apiPath += '&Channels=' + Channels;
        }

        const StatusNames = isArrayWithData(statusNames)
            ? statusNames!.join(',')
            : '';
        if (StatusNames) {
            apiPath += '&StatusNames=' + StatusNames;
        }

        const fullPath: string = this._baseUrl + apiPath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };

    public getGraphCommunications = (
        fromDate: string,
        toDate: string,
        creditorIds?: number[],
        timezoneOffset?: string,
        notificationTypes?: number[],
    ): Observable<any> => {
        let apiPath: string =
            'dashboard/communications?' +
            'FromDate=' +
            fromDate +
            '&ToDate=' +
            toDate;

        if (timezoneOffset) {
            apiPath += '&TZ=' + timezoneOffset;
        }

        const CreditorIds = isArrayWithData(creditorIds)
            ? creditorIds!.join(',')
            : '';

        const NotificationTypes = isArrayWithData(notificationTypes)
            ? notificationTypes!.join(',')
            : '';

        if (CreditorIds) {
            apiPath += '&CreditorIds=' + CreditorIds;
        }

        if (NotificationTypes) {
            apiPath += '&NotificationTypes=' + NotificationTypes;
        }

        const fullPath: string = this._baseUrl + apiPath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };

    public getTableImportIngestions = (
        fromDate: string,
        toDate: string,
        creditorIds?: number[],
        timezoneOffset?: string,
        statuses?: number[],
    ): Observable<any> => {
        let apiPath: string =
            'dashboard/ingestions?' +
            'FromDate=' +
            fromDate +
            '&ToDate=' +
            toDate;

        if (timezoneOffset) {
            apiPath += '&TZ=' + timezoneOffset;
        }

        const CreditorIds = isArrayWithData(creditorIds)
            ? creditorIds!.join(',')
            : '';
        const Statuses = isArrayWithData(statuses) ? statuses!.join(',') : '';

        if (CreditorIds) {
            apiPath += '&CreditorIds=' + CreditorIds;
        }

        if (Statuses) {
            apiPath += '&Statuses=' + Statuses;
        }

        const fullPath: string = this._baseUrl + apiPath;
        const options = this.appService.getHttpHeaderOptions();

        return this.http.get<any>(fullPath, options);
    };
}
